import React, { FC, useMemo } from 'react';

import {
	EvFilters,
	EvSection,
	EvSkeleton,
	EvTitle,
	IFilterValues,
	OptionType,
	TABLE_FILTERS_SR_SUMMARY
} from '@evinced-private/ui-common';

import {
	WFA_DASHBOARD_PAGE_FILTERS_SECTION_ID,
	WFA_DASHBOARD_PAGE_MAIN_TITLE_ID
} from 'data-automation-ids/WfaDashboardAutomationIds';
import omit from 'lodash-es/omit';
import { IWfaLabelOptions } from 'src/apps/wfa/services/WfaDashboardServiceTypes';
import {
	ENVIRONMENT_NO_OPTION_VALUE,
	NO_VALUE_LABEL,
	UNIT_ID_NO_OPTION_VALUE
} from 'src/common/consts/FilterNoOptionConstants';
import { ENVIRONMENT_LABEL_KEY, UNIT_ID_LABEL_KEY } from 'src/common/consts/platformConfigConsts';
import { IFilterOptions } from 'src/common/helpers/DataFilterHelper';
import { flattenObject } from 'src/common/helpers/ObjectsHelper';
import { isSubstringMatch } from 'src/common/helpers/UtilHelper';
import { WFA_SESSIONS_TABLE_ID } from 'src/common/pages/dashboards-container/dashboard_consts';

import { applyWarningsToLabelOptions } from '../../../../helpers/applyWarningsToLabelOptions';
import { useWfaConfiguredValues } from '../../../../hooks/useWfaConfiguredValues';

import {
	createdByFilter,
	getWfaSessionsFilterDefinitions,
	SESSIONS_PERIOD_OPTIONS
} from './WfaSessionsFilterDefinitions';

import './WfaSessionsHeader.scss';

const WFA_PAGE_TITLE = 'Web Flow Analyzer Report';
const FILTER_SKELETON_ITEMS = [
	{ id: 'search', width: '290px', height: '60px' },
	{ id: 'sessions-period', width: '180px', height: '60px' },
	{ id: 'created-by', width: '180px', height: '60px' },
	{ id: 'label-alpha', width: '150px', height: '60px' },
	{ id: 'label-beta', width: '150px', height: '60px' },
	{ id: 'label-gamma', width: '150px', height: '60px' },
	{ id: 'actions', width: '120px', height: '40px' }
] as const;

interface WfaSessionsPageHeaderProps {
	filters: IFilterOptions;
	onFilterChange: (tableId: string, filters: IFilterOptions) => void;
	labelOptions?: IWfaLabelOptions[];
	userOptions?: OptionType[];
	isFilterOptionsLoading: boolean;
}

const EMPTY_CREATED_BY_OPTION: OptionType = { value: ' ', label: 'Unknown' };

export const WfaSessionsPageHeader: FC<WfaSessionsPageHeaderProps> = ({
	filters,
	onFilterChange,
	labelOptions,
	userOptions,
	isFilterOptionsLoading
}) => {
	const createdByOptionsWithFilteredDataBySearchText = useMemo(() => {
		return {
			...createdByFilter,
			componentProps: {
				...createdByFilter.componentProps,
				loadOptions: (searchText: string, callback: (options: OptionType[]) => void) => {
					const filteredUserOptions = userOptions?.filter((item) =>
						isSubstringMatch(item?.label, searchText)
					);
					callback(filteredUserOptions);
				}
			}
		};
	}, [userOptions]);
	const configuredValues = useWfaConfiguredValues();

	const filterDefinitions = useMemo(
		() =>
			getWfaSessionsFilterDefinitions(labelOptions, createdByOptionsWithFilteredDataBySearchText),
		[labelOptions, createdByOptionsWithFilteredDataBySearchText]
	);

	const initialValues = useMemo(() => {
		const selectFilters = omit(filters, 'searchValue');
		labelOptions?.forEach((labelOption) => {
			if (!selectFilters[labelOption.labelKey]) {
				selectFilters[labelOption.labelKey] = [];
			}
		});
		return {
			text: { searchValue: filters?.searchValue },
			select: selectFilters
		};
	}, [filters, labelOptions]);

	const filterOptions = useMemo(() => {
		const staticFilterOptions = {
			sessionsPeriod: SESSIONS_PERIOD_OPTIONS,
			createdBy:
				userOptions?.map((item) => {
					if (typeof item.value === 'string' && item.value === ' ') {
						return EMPTY_CREATED_BY_OPTION;
					}
					return item;
				}) ?? []
		};

		const dyncamicFilterOptions = {};

		labelOptions?.forEach((label) => {
			const options = applyWarningsToLabelOptions(label, configuredValues);
			const labelKeyLower = label.labelKey.toLowerCase();

			if (labelKeyLower === ENVIRONMENT_LABEL_KEY.toLowerCase()) {
				dyncamicFilterOptions[label.labelKey] = [
					...options,
					{ value: ENVIRONMENT_NO_OPTION_VALUE, label: `${NO_VALUE_LABEL}` }
				];
			} else if (labelKeyLower === UNIT_ID_LABEL_KEY.toLowerCase()) {
				dyncamicFilterOptions[label.labelKey] = [
					...options,
					{ value: UNIT_ID_NO_OPTION_VALUE, label: `${NO_VALUE_LABEL}` }
				];
			} else {
				dyncamicFilterOptions[label.labelKey] = options;
			}
		});

		const result = { ...staticFilterOptions, ...dyncamicFilterOptions };

		return result;
	}, [labelOptions, userOptions, configuredValues]);

	const handleApply = (appliedFilters: IFilterValues): void => {
		const flatFilters = flattenObject<IFilterOptions>(appliedFilters);
		onFilterChange(WFA_SESSIONS_TABLE_ID, flatFilters);
	};

	return (
		<div className="wfa-sessions-header">
			<EvTitle
				titleText={WFA_PAGE_TITLE}
				dataAutomationId={WFA_DASHBOARD_PAGE_MAIN_TITLE_ID}
				headingLevel={1}
				className="main-title page-heading"
			/>
			<EvSection
				ariaLabel="Web Flow Analyzer filters"
				ariaDescribedby={TABLE_FILTERS_SR_SUMMARY}
				ariaLive="polite"
				skipLinkId="filters-section"
				className="dashboard-section"
				dataAutomationId={WFA_DASHBOARD_PAGE_FILTERS_SECTION_ID}
			>
				{!isFilterOptionsLoading ? (
					<EvFilters
						className="data-filters"
						onApply={handleApply}
						onReset={handleApply}
						initialValues={initialValues}
						filterOptions={filterOptions}
						filterDefinitions={filterDefinitions}
						isFilterOptionsLoaded={!isFilterOptionsLoading}
						isInitialValuesLoaded={!isFilterOptionsLoading}
					/>
				) : (
					<div className="wfa-sessions-header__filters-skeleton" aria-hidden="true">
						{FILTER_SKELETON_ITEMS.map(({ id, width, height }) => (
							<div key={id} className="filters-skeleton__item">
								<EvSkeleton width={width} height={height} />
							</div>
						))}
					</div>
				)}
			</EvSection>
		</div>
	);
};
